import TypeHelper from '@/helpers/TypeHelper';
import ArrayHelper from '@/helpers/ArrayHelper';
import StringHelper from '@/helpers/StringHelper';
import { Platform } from '@/helpers/PolicySettingsClasses';
import { MatchActions, MatchResultState } from '@/enums/PlaybookEnums';
import { IDataRequestParams, DateColumn } from '@/components/utility/GridUtilities';
import { FilterItemValueGroup } from '@/helpers/FilterClasses';
import { GridConfigurationKey } from '@/enums/CommonEnums';
import { BasicPermissions } from '@/helpers/PermissionsHelper';
import { NameIntValuePair } from '@/helpers/UtilityClasses';
import EnumUtility from '@/enums/EnumUtility';
import { ScanStatusFlags } from '@/pages/scan/ScanClasses';

export enum JobType {
  All = -1,
  Discovery = 1,
  Search = 2,
  DataWatcher = 16
}

// flags enum mapped from api
export enum MatchLocationStates {
  None = 0,
  Overridden = 1
}

export interface IPlaybook {
  matchLocationId: number;
  name: string;
  nodeId: string;
  playbookId: string;
  ruleWeight: number;
  state: MatchResultState;
  editPermission: BasicPermissions;
  locationState: MatchLocationStates;
  searchId: number;
}

// enums mapped from api
export enum LocationTypes {
  None = 0,
  File = 1,
  Email = 2,
  Registry = 4,
  Database = 8,
  Web = 16,
  HiddenWebData = 32,
  Processed = 134217728,
  ExtractedName = 268435456,
  ExtractedIP = 536870912,
  Extracted = 805306368,
  Mapped = 1073741824
}

export enum InternalLocationType {
  None = 0,
  WinMail = 1,
  WinMailAttach = 2,
  OutlookMail = 3,
  OutlookMailAttach = 4,
  PS = 5,
  Firefox = 6,
  Reg = 7,
  ArchiveFile = 8,
  File = 9,
  RecMoved = 10,
  MdbFile = 11,
  WebUrl = 12,
  Dbs = 13,
  ThunderbirdMail = 14,
  ThunderbirdMailAttach = 15,
  MboxMail = 16,
  MboxMailAttach = 17,
  LotusMail = 18,
  LotusMailAttach = 19,
  ExchangeMail = 20,
  ExchangeMailAttach = 21,
  ShadowFile = 22,
  SharePoint = 23,
  CloudStorage = 24,
  CloudEmail = 25,
  CloudEmailAttach = 26,
  NoSql = 27
}

export abstract class InternalLocationTypeUtil
{
  public static toDisplay(type: InternalLocationType, processingDisplay: boolean = false): string
  {
    if (type === undefined || type === null)
    {
      type = 0;
    }

    switch (type)
    {
      case InternalLocationType.WinMail:
        return 'Windows Mail or Outlook Express E-Mail Message';
      case InternalLocationType.WinMailAttach:
        return 'Windows Mail or Outlook Express Attachment';
      case InternalLocationType.OutlookMail:
        return 'Outlook E-Mail Message';
      case InternalLocationType.OutlookMailAttach:
        return 'Outlook Attachment';
      case InternalLocationType.PS:
        return 'Internet Explorer Browser Data';
      case InternalLocationType.Firefox:
        return 'Firefox Browser Data';
      case InternalLocationType.Reg:
        return 'Windows Registry';
      case InternalLocationType.ArchiveFile:
        return 'Compressed File';
      case InternalLocationType.RecMoved:
        return 'Rec Moved';
      case InternalLocationType.MdbFile:
        return 'Mdb File';
      case InternalLocationType.WebUrl:
        return 'Web Page';
      case InternalLocationType.Dbs:
        return 'Database Table';
      case InternalLocationType.ThunderbirdMail:
        return 'Thunderbird E-Mail Message';
      case InternalLocationType.ThunderbirdMailAttach:
        return 'Thunderbird Attachment';
      case InternalLocationType.MboxMail:
        return 'MBOX File E-Mail Message';
      case InternalLocationType.MboxMailAttach:
        return 'MBOX Attachment';
      case InternalLocationType.LotusMail:
        return 'Lotus Notes E-Mail Message';
      case InternalLocationType.LotusMailAttach:
        return 'Lotus Notes Attachment';
      case InternalLocationType.ExchangeMail:
        return 'Microsoft Exchange E-Mail Message';
      case InternalLocationType.ExchangeMailAttach:
        return 'Microsoft Exchange Attachment';
      case InternalLocationType.ShadowFile:
        return 'Shadow File';
      case InternalLocationType.SharePoint:
        return 'SharePoint';
      case InternalLocationType.CloudStorage:
        return 'Cloud Storage';
      case InternalLocationType.CloudEmail:
        return 'Cloud E-Mail Message';
      case InternalLocationType.CloudEmailAttach:
        return 'Cloud E-Mail Attachment';
      case InternalLocationType.NoSql:
        return 'NoSQL Document';
      default:
        return InternalLocationType[type];
    }
  }
}

export enum InternalFileType {
  None = 0,
  Text = 1,
  Pdf = 2,
  Html = 3,
  Rtf = 4,
  Archive = 5,
  Csv = 6,
  Mdb = 7,
  ForceStrings = 8,
  Ocr = 9,
  Dbf = 10,
  IFilter = 31,
  Stringsable = 47,
  Word = 161,
  Excel = 162,
  Ppt = 163,
  Word2007 = 2561,
  Excel2007 = 2562,
  Ppt2007 = 2563
}

export enum LocationActionTypes {
  None = 0x000000,
  Ignore = 0x000001,
  Quarantine = 0x000002,
  Recycle = 0x000004,
  Redact = 0x000008,
  Encrypt = 0x000010,
  Shred = 0x000020,
  OpenOrLaunch = 0x000040,
  PasswordVault = 0x000080, // 128
  IgnoreLocation = 0x000100, // 256
  NotExisting = 0x000200, // 512
  Classify = 0x000400, // 1024
  Script = 0x000800, // 2048
  Permissions = 0x001000, // 4096
  MipLabel = 0x002000, // 8192
  GloballyIgnored = 0x00100000, // 1048576
  // All entries above this point duplicate MatchActions enum in PlaybookEnums.ts. E.g. the actions can be match or location
  NoAction = 0x0000000100000000, // 4294967296
  Assign = 0x0000000200000000, // 8589934592
  Notify = 0x0000000400000000, // 17179869184
  UserAction = 0x0000000800000000 // 34359738368
}

abstract class LocationActionTypesUtil {
  public static toDisplay(type: LocationActionTypes, processingDisplay: boolean = false): string {
    if (type === undefined || type === null) {
      type = 0;
    }

    switch (type) {
      case LocationActionTypes.Quarantine:
        return processingDisplay ? 'Quarantine' : 'Quarantined';
      case LocationActionTypes.Recycle:
        return processingDisplay ? 'Recycle' : 'Recycled';
      case LocationActionTypes.Redact:
        return processingDisplay ? 'Redact' : 'Redacted';
      case LocationActionTypes.Encrypt:
        return processingDisplay ? 'Encrypt' : 'Encrypted';
      case LocationActionTypes.Shred:
        return processingDisplay ? 'Shred' : 'Shredded';
      case LocationActionTypes.OpenOrLaunch:
        return 'Open Or Launch';
      case LocationActionTypes.PasswordVault:
        return 'Password Vault';
      case LocationActionTypes.NotExisting:
        return 'Manual Removal';
      case LocationActionTypes.Classify:
        return processingDisplay ? 'Classify' : 'Classified';
      case LocationActionTypes.Script:
        return processingDisplay ? 'Script' : 'Script Executed';
      case LocationActionTypes.Ignore:
        return processingDisplay ? 'Ignore Matches' : 'Ignored Matches';
      case LocationActionTypes.IgnoreLocation:
        return processingDisplay ? 'Ignore Location' : 'Ignored Location';
      case LocationActionTypes.GloballyIgnored:
        return processingDisplay ? 'Global Ignore' : 'Globally Ignored';
      case LocationActionTypes.Permissions:
        return processingDisplay ? 'Restrict Access' : 'Access Restricted';
      case LocationActionTypes.NoAction:
        return 'No Action Taken';
      case LocationActionTypes.Assign:
        return processingDisplay ? 'Assign' : 'Assigned';
      case LocationActionTypes.Notify:
        return processingDisplay ? 'Notify' : 'Notified';
      case LocationActionTypes.UserAction:
        return 'User Action';
      case LocationActionTypes.MipLabel:
        return 'MIP Label';
      default:
        return LocationActionTypes[<number>type];
    }
  }
}
// end api mapped enums

export enum LocationRowDetailsMode {
  Scans,
  Actions,
  DataTypes
}

// eslint-disable-next-line
export const getLocationActionDisplay = (action: LocationActionTypes, processingDisplay: boolean = false): string => {
  return TypeHelper.isNull(action) || action === LocationActionTypes.None ? 'No Action Taken' : LocationActionTypesUtil.toDisplay(action, processingDisplay);
};

export class LocationRowDetailsData {
  public actionHistory: string[] = [];

  public scanHistory: string[] = [];

  public dataTypes: string[] = [];

  public loading: boolean = false;

  public dataLoaded: boolean = false;

  private initialize(obj: any, topScan: string) {
    const formatScanHistoryItem = (name, value): string => `${name} (${value})`;

    const hasTopScan = !StringHelper.isNullOrEmpty(topScan);
    if (!obj) {
      if (hasTopScan) {
        this.scanHistory.push(formatScanHistoryItem(topScan, 1));
      }
      return;
    }

    // data types returned by the api is just a string array
    this.dataTypes = ArrayHelper.isArraySet(obj.dataTypes) ? obj.dataTypes : [];

    // action history is an array of objects with props action, timestamp.
    if (ArrayHelper.isArraySet(obj.actionHistory)) {
      // eslint-disable-next-line
      this.actionHistory = (obj.actionHistory as any[]).filter(o => !TypeHelper.isNull(o) && ![LocationActionTypes.None, LocationActionTypes.NoAction].includes(o.action)).map(o => {
        // data from the api is already sorted by timestamp with most recent first
        let ret: string = `${DateColumn.toDisplayString(o.timestamp, false)} ${getLocationActionDisplay(o.action)}`;
        if (o.action === LocationActionTypes.Classify) {
          ret += o.hasSearchDetail ? ' (File & Database)' : ' (Database)';
        }
        return ret;
      });
    }

    // scan history is an array of objects with props name and timestamp. UI groups by name and displays a count each scan was run
    if (ArrayHelper.isArraySet(obj.scanHistory)) {
      const scanHistory = obj.scanHistory as any[];
      const data: NameIntValuePair[] = hasTopScan ? [new NameIntValuePair(topScan, 0)] : [];
      scanHistory.forEach(sh => {
        const dataItem = data.find(d => d.name === sh.name);
        if (!dataItem) {
          data.push(new NameIntValuePair(sh.name, 1));
        }
        else {
          dataItem.value++;
        }
      });
      data.filter(d => d.value === 0).forEach(d => d.value = 1); // just in case the top scan was not returned by the api
      this.scanHistory = data.map(d => formatScanHistoryItem(d.name, d.value));
    }

    this.dataLoaded = true;
  }

  public async load($api: any, $service: any, locationRow: ResultsRow) {
    if (this.dataLoaded) {
      return false;
    }

    this.loading = true;
    // delay the load slightly so the loading animation doesn't flash
    setTimeout(async () => {
      try {
        const response = await $api.get(`${$service.SearchController_Api_Url}Search/GetMatchLocationScanData?matchLocationId=${locationRow.locationId}`, null, 'Location details failed to load');
        if (response && !TypeHelper.isNull(response.data)) {
          this.initialize(response.data, locationRow.scanName);
        }
      }
      finally {
        this.loading = false;
      }
    }, 500);
  }
}

export class ResultsRow {
  // Note: this is a full mapping of property names returned from the API. Not all are used in the grid.
  public id: number;

  public matchId: number;

  public locationId: number;

  public location: string;

  public locationType: LocationTypes;

  public internalLocationType: InternalLocationType;

  public locationFileType: string;

  public internalFileType: InternalFileType;

  public actionName: string;

  public actionType: MatchActions;

  public hasHash: boolean;

  public allowResultAction: boolean;

  public assignee: string;

  public allAssignees: string;

  public workflowStatus: number; // WorkflowStatus enum (port from services code if needed)

  public workflowStatusName: string;

  public classificationList: any[]; // Classification object (port from services code if needed)

  public count: number;

  public dataTypes: number; // e.g. count of data types for results by location page

  public hasIndexes: boolean;

  public timestamp: Date;

  public lastTimestamp: Date;

  public endpoint: string;

  public endpointPlatform: Platform;

  public sourceEndpointPlatform: Platform;

  public sourceEndpoint: string;

  public fileOwner: string;

  public user: string;

  public matchType: string;

  public matchTypeId: number;

  public customTypeName: string;

  public dbColumnNames: string;

  public match: string;

  public isMasked: boolean;

  public hasExchangeData: boolean;

  public playbookList: IPlaybook[];

  public scanName: string;

  public scanStatus: number;

  // Local only properties.  E.g. not included in the response data
  public classifications: any[];

  public hasMultipleAssignees: boolean = false;

  public assigneesArray: string[] = [];

  public scanType: JobType;

  public isDiscoveryScan: boolean = false;

  public isSensitiveDataScan: boolean = false;

  public isDataWatcherScan: boolean = false;

  public firstClassificationName: string = null;

  public hasMultiClassifications: boolean = false;

  public weight: number = 0;

  public lastLocationAction: LocationActionTypes;

  // For last location action
  public isConfirmed: boolean = true;

  public hasPlaybooks: boolean = false;

  public hasMultiplePlaybooks: boolean = false;

  public isDiscoveryScanResult: boolean = false;

  public isDiscoveryScanOrNoMatchResult: boolean = false;

  public isSearched: boolean = true; // default if not present.

  public itemClass: string = null;

  public locationDetails = new LocationRowDetailsData();

  constructor(obj: ResultsRow) {
    if (obj) {
      Object.assign(this, obj);
    }

    if (TypeHelper.isNull(this.count)) {
      this.count = 0;
    }

    this.isSearched = TypeHelper.isNull(obj.isSearched) /*just in case*/ ? true : obj.isSearched;

    this.isConfirmed = TypeHelper.isNull(obj.isConfirmed) ? true : obj.isConfirmed;

    this.itemClass = !this.isSearched ? 'scan-history-row' : null;

    // For sorting purposes we need the field name mapped in the grid to be classifications, not classificationList as returned by the API
    this.classifications = obj.classificationList;
    if (!ArrayHelper.isArraySet(this.classifications)) {
      this.classifications = [];
    } else {
      this.firstClassificationName = this.classifications[0].name;
      this.hasMultiClassifications = this.classifications.length > 1;
    }

    // When there are multiple assignees this.assignee wil equal 'Multiple' and this.allAssignees will be populated; otherwise this.assignee has the assigned user name
    this.hasMultipleAssignees = !StringHelper.isNullOrWhiteSpace(this.allAssignees)
      && this.allAssignees.indexOf('\n') > -1;
    if (this.hasMultipleAssignees) {
      this.assigneesArray = StringHelper.splitOnNewLine(this.allAssignees);
    }

    if (!TypeHelper.isNull(this.scanType)) {
      this.isDiscoveryScan = this.scanType === JobType.Discovery;
      this.isDataWatcherScan = this.scanType === JobType.DataWatcher;
      this.isSensitiveDataScan = !this.isDiscoveryScan && !this.isDataWatcherScan;
    }

    // A manual search is a search done from the agent GUI. In this case there is no playbook but the API returns the Discovery ...
    // ... playbook so we clear the list as that is not valid for a manual search.
    if (StringHelper.stringsEqualIgnoreCase(this.scanName, 'manual search')) {
      this.playbookList = null;
    }

    this.hasPlaybooks = ArrayHelper.isArraySet(this.playbookList);
    this.hasMultiplePlaybooks = this.hasPlaybooks && this.playbookList.length > 1;
    // A discovery scan result will always have one playbook with the static guid below
    this.isDiscoveryScanResult = this.hasPlaybooks
      && !this.hasMultiplePlaybooks
      && StringHelper.stringsEqualIgnoreCase('00000000-0000-0000-2000-000000000100', this.playbookList[0].playbookId);

    // Precaution only: services should never include a searchId for discovery scan results, so clear it here if present.
    if (this.isDiscoveryScanResult) {
      this.playbookList.forEach(p => p.searchId = null);
    }

    this.isDiscoveryScanOrNoMatchResult = this.isDiscoveryScanResult || this.count === 0;

    if (this.hasPlaybooks) {
      // Services can return a dummy playbook for display only in the grids. In this case the id is an empty guid so we set to null.
      this.playbookList.filter(p => p.playbookId === TypeHelper.emptyGuid).forEach(p => p.playbookId = null);
    }
  }

  public get lastLocationActionDisplay(): string {
    return getLocationActionDisplay(this.lastLocationAction);
  }

  public get dataTypesDisplay(): any {
    return TypeHelper.isNull(this.dataTypes) ? 'N/A' : this.dataTypes;
  }

  public get internalLocationTypeDisplay(): string
  {
    return InternalLocationTypeUtil.toDisplay(this.internalLocationType);
  }

  public get lastLocationActionIsMip(): boolean {
    return !TypeHelper.isNull(this.lastLocationAction) && this.lastLocationAction === LocationActionTypes.MipLabel;
  }

  public get showMipWarning() : boolean
  {
    return this.lastLocationActionIsMip && !this.isConfirmed;
  }

  public get hasScanErrors(): boolean {
    // status only represents greyhound agent scans. status <= 0 is not applicable and will be the status value for sdm agent scans.
    if (TypeHelper.isNull(this.scanStatus) || this.scanStatus <= 0) {
      return false;
    }
    return EnumUtility.hasFlag(this.scanStatus, ScanStatusFlags.Errors);
  }
}

interface NodesStateInterface {
  nodes: any[];
  nodesType: number;
}

interface DateRangeInterface {
  startDate: string;
  endDate: string;
  operator: number;
}

interface GlobalFilterInterface {
  nodes: NodesStateInterface;
  range: DateRangeInterface;
  searchFilterIds: any[];
  saveMode: number;
}

interface EndpointsGridFilterInterface {
  global: GlobalFilterInterface;
  filter: FilterItemValueGroup;
  filterConfigurationKey: string;
  columnFilters: any;
  gridConfigurationKey: string;
}

export class EndpointsDataLoadRequestParam {
  info: IDataRequestParams;

  filter: EndpointsGridFilterInterface;

  partialDataConfig: any;

  cancellationToken: string;

  isCheckAll: boolean;

  checkAllMaxAllowed: number;

  scanType: number;

  scanIds: string[];

  includeNoMatchResults: boolean;

  locationId: any = undefined;

  constructor(isDataWatcherResults: boolean = false, scanType: number = null, includeNoMatchResults: boolean = false, groupByLocation: boolean = false) {
    this.filter = {
      global: {
        nodes: {
          nodes: [0],
          nodesType: 5
        },
        range: null,
        searchFilterIds: [],
        saveMode: 0,
      },
      filter: null,
      filterConfigurationKey: isDataWatcherResults ? 'DataWatcherResults' : 'ScanResults',
      gridConfigurationKey: isDataWatcherResults ? GridConfigurationKey[GridConfigurationKey.DataWatcherResultsGrid] : GridConfigurationKey[GridConfigurationKey.ScanResultsGrid],
      columnFilters: null,
    }
    if (groupByLocation) {
      this.filter.gridConfigurationKey = isDataWatcherResults ? GridConfigurationKey[GridConfigurationKey.DataWatcherResultsGridByLocation] : GridConfigurationKey[GridConfigurationKey.ScanResultsGridByLocation];
    }
    this.partialDataConfig = null;
    this.cancellationToken = TypeHelper.getGuid();
    this.isCheckAll = false;
    this.checkAllMaxAllowed = 0;
    this.scanType = TypeHelper.isNull(scanType) ? undefined : scanType;
    this.includeNoMatchResults = includeNoMatchResults;
    if (!groupByLocation) {
      return;
    }

    // partial data config tells the API to group the data by location
    this.partialDataConfig = {
      masterColumns: {
        columns: [
          'Location',
          'LocationFileType',
          'Endpoint',
          'EndpointPlatform',
          'SourceEndpoint',
          'SourceEndpointPlatform',
          'FileOwner',
          'User',
          'LocationId',
          'LocationType',
          'InternalFileType',
          'InternalLocationType',
          'Assignee',
          'Classifications',
          //'DataHashType',
          'PlaybookResultJSON',
          'ScanName',
          'ScanType',
          'ScanStatus',
          'Weight',
          'LastLocationAction',
          'IsConfirmed',
          'AllowResultAction',
          'HasExchangeData',
          'IsSearched',
          // The 2 playbook fields below are not returned to the UI but they must be included as the services use them to query for the data and inject ...
          // ... the playbook into the JSON for location rows where the playbook is still involved in an active scan
          'PlaybookId',
          'PlaybookName'
        ],
        columnsToAlwaysLoad: ['AllowResultAction'],
        useMainTableId: true
      }
    };
  }
}
